import { useCallback, useEffect, useState, useMemo } from 'react'
import { Copy, Plus } from 'lucide-react'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { DeleteConnectionDialog } from '@/screens/accounts/DeleteConnectionDialog'
import { EditConnectionDialog } from '@/screens/accounts/EditConnectionDialog'
import type {
  BrokerConnectionDto,
  BrokerLoginDto,
  BrokerProviderDto,
  ReadinessResultDto,
  StaticIpAssignmentDto,
  StaticIpsResponse,
} from '@apna/shared'
import { api } from '@/lib/api'
import { notifyOk } from '@/components/Toast'
import { cn } from '@/lib/utils'
import { AccountsTable } from '@/screens/accounts/AccountsTable'
import { BrokerSelectModal } from '@/screens/accounts/BrokerSelectModal'
import { ConnectBroker } from '@/screens/accounts/ConnectBroker'
import { RotateSecretsDialog } from '@/screens/accounts/RotateSecretsDialog'
import { StaticIpList } from '@/screens/accounts/StaticIpList'
import { StaticIpsTable } from '@/screens/accounts/StaticIpsTable'

/**
 * Accounts — laid out as trading-journal's "Accounts & Risk" screen: title,
 * tab row with the primary action on the right, and a table as the first
 * thing on the page.
 *
 * Tabs differ because the domains do. The journal has MTF / Funding and Forex;
 * neither exists here. ApnaAlgo's second concept is CREDENTIALS, which the
 * journal does not need to separate — XTS issues two pairs per broker and a
 * dealer pair is shared across many accounts, so credentials have a lifecycle
 * of their own.
 *
 * The bottom metrics strip is deliberately absent. Every figure it would show
 * (total capital, today's P&L) depends on funds or positions, and funds are
 * unavailable on dealer logins while positions are Phase 6.
 */

/*
 * Two tabs, not three.
 *
 * Credentials moved INTO the broker edit form. Nobody thinks "I will manage
 * a credential" — they think "I will connect this account", and a tab of its
 * own made the user hold two things in their head at once: which key set
 * exists, and which account points at it.
 */
type ActiveTab = 'brokers' | 'static-ips'

const TABS: { key: ActiveTab; label: string }[] = [
  { key: 'brokers', label: 'Brokers' },
  { key: 'static-ips', label: 'Static IPs' },
]

export default function Accounts() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('brokers')
  /*
   * Which account leaves from which address, for the ASSIGNED IP column.
   * Read here rather than in the table so the table stays presentational,
   * and failing quietly on purpose: a column that cannot load must not take
   * the accounts list down with it.
   */
  const [assignments, setAssignments] = useState<StaticIpAssignmentDto[]>([])
  useEffect(() => {
    void api
      .get<StaticIpsResponse>('/broker/static-ips')
      .then((r) => setAssignments(r.assignments))
      .catch(() => setAssignments([]))
  }, [])

  const [providers, setProviders] = useState<BrokerProviderDto[]>([])
  const [logins, setLogins] = useState<BrokerLoginDto[]>([])
  const [connections, setConnections] = useState<BrokerConnectionDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [pickerOpen, setPickerOpen] = useState(false)
  const [connecting, setConnecting] = useState<BrokerProviderDto | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [banner, setBanner] = useState<ReadinessResultDto | null>(null)
  const [editing, setEditing] = useState<BrokerConnectionDto | null>(null)
  const [rotating, setRotating] = useState<BrokerLoginDto | null>(null)

  /*
   * Every action that changes or destroys something goes through one
   * confirmation component rather than `window.confirm`.
   *
   * `confirm()` cannot say WHAT is kept and what is lost, cannot show a
   * pending state while the request runs, and is suppressible by the browser.
   * Removing a broker account on a trading desk deserves a sentence
   * explaining that the order history survives.
   */
  const [confirmState, setConfirmState] = useState<{
    title: string
    description: React.ReactNode
    confirmLabel: string
    pendingLabel: string
    destructive: boolean
    run: () => Promise<void>
  } | null>(null)
  const [confirmPending, setConfirmPending] = useState(false)

  /*
   * How many connections point at each login. A dealer pair legitimately
   * backs several accounts ([B1]), and rotating its secret logs out every
   * one of them — so the count has to reach the edit form.
   */
  const usageByLogin = useMemo(() => {
    const out: Record<string, number> = {}
    for (const c of connections) {
      for (const id of [c.interactiveLogin?.id, c.marketDataLogin?.id]) {
        if (id) out[id] = (out[id] ?? 0) + 1
      }
    }
    return out
  }, [connections])

  const load = useCallback(async () => {
    try {
      const [p, l, c] = await Promise.all([
        api.get<{ items: BrokerProviderDto[] }>('/broker/providers'),
        api.get<{ items: BrokerLoginDto[] }>('/broker/logins'),
        api.get<{ items: BrokerConnectionDto[] }>('/broker/connections'),
      ])
      setProviders(p.items)
      setLogins(l.items)
      setConnections(c.items)
      setError(null)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  /**
   * Report a readiness result: a TOAST when it passed, the banner when it
   * did not.
   *
   * ────────────────────────────────────────────────────────────────────────
   * A SUCCESS HAS NOTHING FOR THE READER TO DO.
   *
   * The banner used to carry every result, including this one, so a working
   * login ended in three lines of amber about what `ORDER_BOOK` does not
   * prove — true, and shown after EVERY login until dismissed by hand. A
   * caveat that appears when nothing is wrong stops being read, and takes
   * the failures printed in the same place down with it.
   *
   * A failure keeps the banner, and must: it carries the broker's own words
   * untruncated, the copy button and the remedy, and a trader may need to
   * read it twice and paste it to support. That is not a toast.
   *
   * The probe is still named in the toast's second line, because "connected"
   * and "the RMS priced an order" are different claims and the weaker one
   * must not silently read as the stronger.
   */
  const reportReadiness = (r: ReadinessResultDto, label: string): void => {
    if (!r.ok) {
      setBanner(r)
      return
    }
    setBanner(null)
    notifyOk(
      `${label} connected`,
      r.probe === 'ORDER_MARGIN'
        ? 'Order capability confirmed by the broker.'
        : 'Credentials, IP and order-endpoint access confirmed.',
    )
  }

  const run = async (id: string, path: 'readiness' | 'reconnect') => {
    setBusyId(id)
    setBanner(null)
    try {
      const r = await api.post<ReadinessResultDto>(`/broker/connections/${id}/${path}`)
      reportReadiness(r, connections.find((c) => c.id === id)?.label ?? 'Account')
      await load()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setBusyId(null)
    }
  }

  /*
   * DELETING AN ACCOUNT IS NOT A SETTING, so it does not use the generic
   * confirm. It needs counts read from the server first — an open position
   * REFUSES the delete outright, and the phrase the user types names how
   * many trades go with it. See `DeleteConnectionDialog`.
   */
  const [removing, setRemoving] = useState<BrokerConnectionDto | null>(null)

  const askRemoveLogin = (id: string, label: string) => {
    const usedBy = connections.filter(
      (c) => c.interactiveLogin?.id === id || c.marketDataLogin?.id === id,
    )
    setConfirmState({
      title: `Delete the credential "${label}"?`,
      description:
        usedBy.length > 0 ? (
          <>
            This credential is still attached to{' '}
            <span className="font-medium text-foreground">
              {usedBy.length} account{usedBy.length > 1 ? 's' : ''}
            </span>{' '}
            ({usedBy.map((c) => c.clientCode).join(', ')}). Detach it there first — deleting a
            dealer credential would silently disarm every account it serves.
          </>
        ) : (
          <>
            The encrypted key and secret are removed and cannot be recovered. You would need to
            paste them again from the broker.
          </>
        ),
      confirmLabel: 'Delete credential',
      pendingLabel: 'Deleting…',
      destructive: true,
      run: async () => {
        await api.del(`/broker/logins/${id}`)
        await load()
      },
    })
  }

  const askLogout = (c: BrokerConnectionDto) => {
    setConfirmState({
      title: `End the broker session for ${c.label}?`,
      description: (
        <>
          The stored token is discarded and the session is closed at{' '}
          <span className="font-medium text-foreground">{c.providerKey}</span>. The account stays
          configured and its credential is untouched — the next action logs in again.
          <br />
          <br />
          Worth doing when you are done for the day: XTS allows a limited number of concurrent
          sessions per app key, so a session nobody is using still holds a slot.
        </>
      ),
      confirmLabel: 'End session',
      pendingLabel: 'Closing…',
      destructive: false,
      run: async () => {
        setBusyId(c.id)
        setBanner(null)
        try {
          const r = await api.post<{ brokerAcknowledged: boolean; detail: string }>(
            `/broker/connections/${c.id}/logout`,
          )
          /*
           * Same rule as readiness: a toast when the broker confirmed, the
           * banner when it did not. The local state is clean either way, but
           * "we think it is closed" is not "the broker says it is closed",
           * and only the second of those is a result with nothing to read.
           */
          if (r.brokerAcknowledged) {
            setBanner(null)
            notifyOk(`${c.label} session closed`, r.detail)
          } else {
            setBanner({
              ok: false,
              probe: 'ORDER_BOOK',
              isInvestorClient: c.interactiveLogin?.isInvestorClient ?? null,
              detail: r.detail,
              checkedAt: new Date().toISOString(),
            })
          }
          await load()
        } finally {
          setBusyId(null)
        }
      },
    })
  }

  const askReconnect = (c: BrokerConnectionDto) => {
    setConfirmState({
      title: `Re-login ${c.label}?`,
      description: (
        <>
          This discards the stored session token and authenticates again against{' '}
          <span className="font-medium text-foreground">{c.providerKey}</span>.
          <br />
          <br />
          Harmless, but not free: several Indian brokers rate-limit or lock an account that logs
          in repeatedly.
        </>
      ),
      confirmLabel: 'Re-login',
      pendingLabel: 'Logging in…',
      destructive: false,
      run: async () => {
        await run(c.id, 'reconnect')
      },
    })
  }

  const runConfirmed = async () => {
    if (!confirmState) return
    setConfirmPending(true)
    try {
      await confirmState.run()
      setConfirmState(null)
    } catch (err) {
      setError((err as Error).message)
      setConfirmState(null)
    } finally {
      setConfirmPending(false)
    }
  }

  // The connect flow takes over the whole page, as it does in the journal —
  // it is a setup task, not something to do inside a modal over a live table.
  if (connecting) {
    return (
      <div className="h-full overflow-auto p-4">
        <ConnectBroker
          provider={connecting}
          onBack={() => setConnecting(null)}
          onDone={() => {
            setConnecting(null)
            void load()
          }}
        />
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto p-4" data-testid="accounts-page">
      <div className="w-full space-y-4 pb-6 text-foreground">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h1 className="truncate text-xl font-semibold leading-none tracking-tight text-foreground">
              Accounts
            </h1>
            <p className="mt-1 text-xs text-muted-foreground/80">
              Manage broker accounts and the egress they trade through. Credentials live inside each account.
            </p>
          </div>
        </div>

        <div className="flex flex-row flex-nowrap items-center justify-between gap-1.5 sm:gap-2">
          <div className="inline-flex h-auto w-fit rounded-lg border border-border/60 bg-muted/20 p-0.5">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                data-state={activeTab === t.key ? 'active' : 'inactive'}
                data-testid={`tab-${t.key}`}
                className={cn(
                  'rounded-md px-1.5 py-1.5 text-[10.5px] font-medium transition-colors sm:px-3 sm:text-[12px] lg:px-4',
                  activeTab === t.key
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="ml-auto flex shrink-0 items-center justify-end gap-1.5 sm:gap-2">
            {activeTab !== 'static-ips' && (
              <button
                onClick={() => setPickerOpen(true)}
                disabled={providers.length === 0}
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground shadow-sm transition hover:opacity-90 disabled:opacity-40"
                data-testid="add-broker"
              >
                <Plus className="h-4 w-4" />
                Add Broker
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4">
            <p className="text-sm text-destructive">{error}</p>
            <button
              onClick={() => void load()}
              className="mt-3 h-9 rounded-lg border px-4 text-sm transition hover:bg-accent"
            >
              Try again
            </button>
          </div>
        )}

        {/*
          Readiness is reported as its own banner rather than folded into the
          status pill, because the two are different claims. `ORDER_MARGIN`
          proves the broker priced an order through its RMS; `ORDER_BOOK` only
          proves the order resource answered with this token from this IP. The
          banner names which probe ran and is amber for anything weaker than
          ORDER_MARGIN, so a fallback never reads as a pass.
        */}
        {/*
          The readiness banner.

          NOTHING IS TRUNCATED here. The previous version clipped the message
          with CSS and hid the one sentence that identified the problem —
          "IP address does not match the login IP" — behind an ellipsis. It
          now shows the remedy first, then the broker's own words in full,
          with a copy button and a pointer to the console for everything else.
        */}
        {banner && (
          <div
            className={cn(
              'rounded-lg border px-3 py-2.5',
              !banner.ok
                ? 'border-destructive/30 bg-destructive/5'
                : banner.probe === 'ORDER_MARGIN'
                  ? 'border-success/30 bg-success/5'
                  : 'border-warning/30 bg-warning/5',
            )}
            data-testid="readiness-banner"
          >
            <div className="flex items-start gap-2">
              <span
                className={cn(
                  'shrink-0 rounded border px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide',
                  !banner.ok
                    ? 'border-destructive/30 text-destructive'
                    : banner.probe === 'ORDER_MARGIN'
                      ? 'border-success/30 text-success'
                      : 'border-warning/30 text-warning',
                )}
              >
                {banner.failureKind ?? (banner.probe === 'ORDER_MARGIN' ? 'order capability' : banner.probe)}
              </span>

              <p
                className={cn(
                  'min-w-0 flex-1 whitespace-pre-wrap break-words text-xs leading-relaxed',
                  !banner.ok ? 'text-destructive' : banner.probe === 'ORDER_MARGIN' ? 'text-success' : 'text-warning',
                )}
              >
                {banner.detail}
              </p>

              <button
                onClick={() =>
                  void navigator.clipboard.writeText(
                    [banner.detail, banner.raw, `probe=${banner.probe}`, `at=${banner.checkedAt}`]
                      .filter(Boolean)
                      .join('\n'),
                  )
                }
                className="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
                title="Copy full detail"
              >
                <Copy size={13} />
              </button>

              <button
                onClick={() => setBanner(null)}
                className="shrink-0 text-xs text-muted-foreground transition-colors hover:text-foreground"
              >
                Dismiss
              </button>
            </div>

            {/* The broker's raw response, verbatim and wrapped. */}
            {banner.raw && banner.raw !== banner.detail && (
              <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-all rounded border bg-background/60 px-2 py-1.5 font-mono text-[10.5px] leading-relaxed text-muted-foreground">
                {banner.raw}
              </pre>
            )}

            <p className="mt-2 text-[10px] text-muted-foreground">
              The full request and the broker's reply are in the server log.
            </p>
          </div>
        )}

        {activeTab === 'brokers' && (
          <AccountsTable
            rows={connections}
            assignments={assignments}
            loading={loading}
            busyId={busyId}
            onReadiness={(c) => void run(c.id, 'readiness')}
            onLogout={askLogout}
            onReconnect={askReconnect}
            onEdit={setEditing}
            onDelete={setRemoving}
          />
        )}

        {activeTab === 'static-ips' && (
          <div className="grid gap-4">
            {/*
              The LIST first, because the question this tab answers is "which
              account leaves from where". The platform-egress form beneath it
              still configures the shared endpoint an unassigned account
              falls back to.
            */}
            <StaticIpList />
            <StaticIpsTable />
          </div>
        )}
      </div>

      <EditConnectionDialog
        connection={editing}
        interactiveLogins={logins.filter((l) => l.role === 'INTERACTIVE')}
        marketDataLogins={logins.filter((l) => l.role === 'MARKET_DATA')}
        usageByLogin={usageByLogin}
        onLoginsChanged={() => void load()}
        onDeleteLogin={askRemoveLogin}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          void load()
        }}
      />

      {rotating && (
        <RotateSecretsDialog
          login={rotating}
          attachedCount={
            connections.filter(
              (c) =>
                c.interactiveLogin?.id === rotating.id || c.marketDataLogin?.id === rotating.id,
            ).length
          }
          onClose={() => setRotating(null)}
          onDone={() => {
            setRotating(null)
            void load()
          }}
        />
      )}

      <DeleteConnectionDialog
        connection={removing}
        onClose={() => setRemoving(null)}
        onDeleted={() => void load()}
      />

      {confirmState && (
        <ConfirmDialog
          open
          onOpenChange={(o) => !o && setConfirmState(null)}
          title={confirmState.title}
          description={confirmState.description}
          confirmLabel={confirmState.confirmLabel}
          pendingLabel={confirmState.pendingLabel}
          destructive={confirmState.destructive}
          isPending={confirmPending}
          onConfirm={() => void runConfirmed()}
        />
      )}

      <BrokerSelectModal
        open={pickerOpen}
        providers={providers}
        onClose={() => setPickerOpen(false)}
        onSelect={(p) => {
          setPickerOpen(false)
          setConnecting(p)
        }}
      />
    </div>
  )
}
